<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { OWNER_CONTACT_REASONS } from '../utils/imovelShowConstants';

defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  step: {
    type: String,
    default: 'terms',
  },
  weeklyCountLabel: {
    type: String,
    default: '',
  },
  limitReached: {
    type: Boolean,
    default: false,
  },
  contactLoading: {
    type: Boolean,
    default: false,
  },
  revealLoading: {
    type: Boolean,
    default: false,
  },
  companyName: {
    type: String,
    default: '',
  },
  contact: {
    type: Object,
    default: null,
  },
  contactPhone: {
    type: String,
    default: '',
  },
  contactEmail: {
    type: String,
    default: '',
  },
  whatsappHref: {
    type: String,
    default: null,
  },
  phoneHref: {
    type: String,
    default: null,
  },
  whatsappMessage: {
    type: String,
    default: '',
  },
  errorMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'close',
  'acceptTerms',
  'back',
  'reveal',
  'selectReason',
  'trackChannel',
]);

const ownerRevealForm = defineModel('ownerRevealForm', {
  type: Object,
  required: true,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisDrawer
    :show="show"
    title="Telefone do proprietário"
    side="right"
    size="md"
    @close="emit('close')"
  >
    <div class="grid gap-4">
      <div
        v-if="step === 'terms'"
        class="grid gap-4 text-[13px] text-lais-text-body"
      >
        <div class="flex items-start gap-3">
          <span
            class="grid size-11 shrink-0 place-items-center rounded-full bg-amber-50 text-amber-700"
          >
            <span class="i-lucide-triangle-alert size-5" aria-hidden="true" />
          </span>
          <div>
            <p
              class="font-redHatDisplay text-[18px] font-semibold text-lais-text"
            >
              Termos para revelar contato
            </p>
            <p class="mt-1 text-[12px] leading-relaxed text-lais-text-muted">
              O contato do proprietário é usado apenas para conduzir este
              atendimento e fica registrado para auditoria.
            </p>
          </div>
        </div>

        <div
          class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3"
        >
          <p class="font-semibold text-lais-text">
            {{ weeklyCountLabel }}
          </p>
          <p class="mt-1 text-[12px] leading-relaxed">
            Todas as solicitações são registradas e monitoradas para proteger os
            dados do proprietário.
          </p>
        </div>

        <div
          v-if="limitReached"
          class="rounded-lais-sm border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800"
        >
          <p class="font-semibold">Limite semanal atingido</p>
          <p class="mt-1 text-[12px] leading-relaxed">
            Os contatos ficarão disponíveis novamente no próximo ciclo semanal.
          </p>
        </div>

        <div class="grid gap-3">
          <p class="font-semibold text-lais-text">
            Ao solicitar o contato, você concorda que:
          </p>
          <p>
            <strong>Usará os dados apenas para fins autorizados</strong>
            neste atendimento imobiliário e dentro das regras da parceria com a
            {{ companyName }}.
          </p>
          <p>
            <strong>Não compartilhará nem reutilizará o contato</strong>
            fora desta operação. O uso indevido, inclusive após o encerramento
            da parceria, pode gerar sanções contratuais e legais.
          </p>
        </div>
      </div>

      <div v-else-if="step === 'reason'" class="grid gap-4">
        <div
          class="rounded-lais-sm border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] leading-relaxed text-amber-800"
        >
          Verificaremos junto ao proprietário se o contato foi realizado sob o
          motivo selecionado.
        </div>

        <div class="grid gap-2">
          <label
            v-for="reason in OWNER_CONTACT_REASONS"
            :key="reason.value"
            class="flex cursor-pointer items-center justify-between gap-3 rounded-lais-sm border border-lais-border-soft px-3 py-3 text-[13px] transition-colors hover:border-lais-primary"
            :class="
              ownerRevealForm.reason === reason.value
                ? 'border-lais-primary bg-lais-primary-50'
                : 'bg-white'
            "
          >
            <span class="font-semibold text-lais-text">
              {{ reason.label }}
            </span>
            <input
              v-model="ownerRevealForm.reason"
              type="radio"
              name="owner-contact-reason"
              :value="reason.value"
              class="size-4 border-lais-border text-lais-primary focus:ring-lais-primary"
              @change="emit('selectReason', reason)"
            />
          </label>
        </div>
      </div>

      <div v-else class="grid gap-4">
        <div class="rounded-lais border border-lais-border bg-lais-bg-soft p-4">
          <div class="flex items-center gap-3">
            <span
              class="grid size-11 shrink-0 place-items-center rounded-full bg-white text-lais-primary shadow-sm"
            >
              <span class="i-lucide-user-round size-5" aria-hidden="true" />
            </span>
            <div class="min-w-0">
              <p class="truncate text-[14px] font-semibold text-lais-text">
                {{ contact?.name || 'Proprietário' }}
              </p>
              <p class="mt-1 text-[13px] text-lais-text-body">
                {{ contactPhone || contactEmail || 'Contato revelado' }}
              </p>
            </div>
          </div>
        </div>

        <div class="grid gap-2">
          <p class="text-[13px] font-semibold text-lais-text">
            Como entrar em contato
          </p>
          <div
            class="divide-y divide-lais-border-soft rounded-lais border border-lais-border bg-white"
          >
            <div class="flex gap-3 px-3 py-3">
              <span
                class="grid size-9 shrink-0 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
              >
                <span class="i-lucide-phone size-4" aria-hidden="true" />
              </span>
              <p class="text-[12px] leading-relaxed text-lais-text-body">
                Em caso de urgência, ao ligar, informe que você é corretor(a)
                parceiro(a) da {{ companyName }} e que o contato é sobre este
                imóvel.
              </p>
            </div>
            <div class="flex gap-3 px-3 py-3">
              <span
                class="grid size-9 shrink-0 place-items-center rounded-full bg-emerald-50 text-emerald-700"
              >
                <span
                  class="i-lucide-message-circle size-4"
                  aria-hidden="true"
                />
              </span>
              <p class="text-[12px] leading-relaxed text-lais-text-body">
                Para contato via WhatsApp, envie a mensagem padrão de
                identificação antes de continuar a conversa.
              </p>
            </div>
          </div>

          <p class="text-[13px] font-semibold text-lais-text">
            Mensagem de identificação via WhatsApp
          </p>
          <p
            class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2 text-[12px] leading-relaxed text-lais-text-body"
          >
            {{ whatsappMessage }}
          </p>
        </div>

        <div class="grid gap-2">
          <a
            v-if="whatsappHref"
            :href="whatsappHref"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex min-h-10 items-center justify-center gap-2 rounded-lais-sm bg-lais-primary px-3 text-[13px] font-semibold text-white transition-colors hover:bg-lais-primary/90"
            @click="emit('trackChannel', 'whatsapp')"
          >
            <span class="i-lucide-message-circle size-4" aria-hidden="true" />
            Enviar mensagem
          </a>
          <a
            v-if="phoneHref"
            :href="phoneHref"
            class="inline-flex min-h-10 items-center justify-center gap-2 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text transition-colors hover:border-lais-primary hover:text-lais-primary"
            @click="emit('trackChannel', 'phone')"
          >
            <span class="i-lucide-phone size-4" aria-hidden="true" />
            Ligação
          </a>
          <a
            v-if="contactEmail"
            :href="`mailto:${contactEmail}`"
            class="inline-flex min-h-10 items-center justify-center gap-2 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text transition-colors hover:border-lais-primary hover:text-lais-primary"
            @click="emit('trackChannel', 'email')"
          >
            <span class="i-lucide-mail size-4" aria-hidden="true" />
            E-mail
          </a>
        </div>
      </div>

      <p
        v-if="errorMessage"
        class="rounded-lais-sm bg-red-50 px-3 py-2 text-[12px] leading-relaxed text-red-700"
      >
        {{ errorMessage }}
      </p>
    </div>

    <template #footer>
      <div
        v-if="step === 'terms'"
        class="flex flex-col gap-2 sm:flex-row sm:justify-end"
      >
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          Fechar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-check"
          :disabled="limitReached || contactLoading"
          @click="emit('acceptTerms')"
        >
          Aceitar e continuar
        </LaisPrimaryButton>
      </div>
      <div
        v-else-if="step === 'reason'"
        class="flex flex-col gap-2 sm:flex-row sm:justify-between"
      >
        <LaisPrimaryButton variant="ghost" @click="emit('back')">
          Voltar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-eye"
          :is-loading="revealLoading"
          :disabled="!ownerRevealForm.reason || revealLoading"
          @click="emit('reveal')"
        >
          Solicitar
        </LaisPrimaryButton>
      </div>
      <div v-else class="flex justify-end">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          Fechar
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisDrawer>
</template>
